import { useNavigate } from 'react-router';
import { useTenantContext } from '../../../contexts/TenantContext';
import useCleverTap from '../../../hooks/useCleverTap';
import { CompanyType, UserRole } from '../../../types/dataTypes';
import { useUserContext } from '../../../contexts/UserContext';
import { useFetchFlipper } from '../../../services/merchants';
import { ContractStatus } from '../../../helpers/TypeConstant';
import { toolRedirect } from '../../../services/http';
import {
  CLEVERTAP_ADDITIONAL,
  CLEVERTAP_SCREEN_NAME,
  CLEVERTAP_SECTION_NAME,
  CLEVERTAP_TAPPED_ON,
} from '../../../helpers/ClevertapKeys';
import { addNewMerchantRedirect, addProductsModal } from './helpers';
import { ItemType } from 'antd/lib/menu/hooks/useItems';
import { Button, Dropdown, Menu, ModalFunctions } from 'fave-ui';
import { SearchBarQueryString } from '../../../components/common';
import { Plus } from 'phosphor-react';
import style from './style.module.css';
import { FetchMcProductListResponse } from '../../../services/MySubmissions';

export const ActionsMenuItems = ({
  companyData,
  getCompanyStampCardDrafts,
}: {
  companyData: CompanyType;
  getCompanyStampCardDrafts: (
    id: number,
  ) => Promise<FetchMcProductListResponse>;
}) => {
  const { selectedTenant } = useTenantContext();
  const cleverTap = useCleverTap();
  const navigate = useNavigate();
  const { user, getPageSettings } = useUserContext();
  const pageSettings = getPageSettings('merchant_connect');
  const enableIppOnboarding = !!pageSettings?.ipp_onboarding;
  const { data: isFavePaymentModeEnabled } = useFetchFlipper({
    key: `fave_payment_mode_enabled_${selectedTenant}`,
  });
  const { data: isFavePassOnboardingDisabled } = useFetchFlipper({
    key: 'disable_fave_pass_onboarding',
  });
  const { data: isStampCardOnboardingDisabled } = useFetchFlipper({
    key: 'hide_stamp_cards_onboarding',
  });
  const { data: isUobPointRedemptionOnboardingDisabled } = useFetchFlipper({
    key: `disable_uob_point_redemption_for_${selectedTenant}`,
  });

  const isFaveMerchant = companyData.merchant_type === 'fave';
  const allowedRoles =
    user?.role === UserRole.SuperAdmin ||
    user?.role === UserRole.BusinessDevelopmentManager;
  const allowedContractStatus =
    companyData.contract_status === ContractStatus.NotSent ||
    companyData.contract_status === ContractStatus.Cancelled;
  const noOngoingContractMigration = !companyData.ongoing_contract_migration;
  const canGenerateMasterContract =
    isFaveMerchant &&
    (allowedRoles || (noOngoingContractMigration && allowedContractStatus));

  const menuItems = [
    {
      key: 'view_merchant',
      label: 'View Merchant',
      onClick: () =>
        toolRedirect(`${window.location.pathname}/${companyData.id}/outlets?`),
    },
    {
      key: 'product_dashboard',
      label: 'Product Dashboard',
      onClick: () => {
        cleverTap.tapped.push({
          screen_name: CLEVERTAP_SCREEN_NAME.MY_MERCHANT,
          tapped_on: CLEVERTAP_TAPPED_ON.BUTTON_PRODUCT_DASHBOARD,
          section_name: CLEVERTAP_SECTION_NAME.ACTIONS_MENU,
        });

        navigate(`${companyData.id}/product_dashboard`);
      },
    },
    canGenerateMasterContract && {
      key: 'generate_master_contract',
      label: 'Generate Master Contract',
      onClick: () => {
        cleverTap.tapped.push({
          screen_name: CLEVERTAP_SCREEN_NAME.MY_MERCHANT,
          tapped_on: CLEVERTAP_TAPPED_ON.BUTTON_GENERATE_MASTER_CONTRACT,
          section_name: CLEVERTAP_SECTION_NAME.ACTIONS_MENU,
          platform: CLEVERTAP_ADDITIONAL.MERCHANT_CONNECT,
        });

        toolRedirect(
          `${window.location.pathname}/${companyData.id}/contract_migration`,
        );
      },
    },
    {
      key: 'add_new_product',
      label: 'Add New Product',
      onClick: async () => {
        const isFaveMerchant = companyData.merchant_type === 'fave';
        const stampCardDraftData = isFaveMerchant
          ? await getCompanyStampCardDrafts(companyData.id)
          : undefined;

        addProductsModal({
          cleverTap,
          companyData,
          stampCardDraftData,
          enableIppOnboarding,
          isFavePaymentModeEnabled,
          isFavePassOnboardingDisabled,
          isStampCardOnboardingDisabled,
          isUobPointRedemptionOnboardingDisabled,
          navigateFn: navigate,
        });
      },
    },
  ].filter(Boolean) as ItemType[];

  const menu = <Menu items={menuItems} />;

  return (
    <Dropdown overlay={menu} trigger={['hover']}>
      <Button type={'text'} onClick={e => e.preventDefault()}>
        Actions
      </Button>
    </Dropdown>
  );
};

export const AddMerchantAndSearchButton = () => {
  const cleverTap = useCleverTap();
  const { getPageSettings } = useUserContext();
  const pageSettings = getPageSettings('merchant_connect');

  const enableFastPay = !!pageSettings?.fastpay_onboarding;

  const handleAddNewMerchant = () => {
    cleverTap.tapped.push({
      screen_name: CLEVERTAP_SCREEN_NAME.MY_MERCHANT,
      section_name: CLEVERTAP_SECTION_NAME.ADD_MENU,
      platform: CLEVERTAP_ADDITIONAL.MERCHANT_CONNECT,
      tapped_on: CLEVERTAP_TAPPED_ON.BUTTON_ADD_NEW_MERCHANT,
    });

    enableFastPay
      ? ModalFunctions.info({
          className: style.addNewMerchantModal,
          title: 'Add new merchant',
          content: <p>Which type of merchant is this?</p>,
          icon: null,
          okText: 'Fave',
          okButtonProps: {
            onClick: () => addNewMerchantRedirect('fave'),
          },
          cancelText: 'FastPay',
          cancelButtonProps: {
            onClick: () => addNewMerchantRedirect('fastpay'),
            className: style.btnFastpay,
          },
          width: 360,
          centered: true,
        })
      : addNewMerchantRedirect('fave');
  };

  return (
    <div className={'flex'}>
      <SearchBarQueryString />
      <Button
        size={'large'}
        type={'primary'}
        shape={'circle'}
        icon={<Plus />}
        onClick={handleAddNewMerchant}
      />
    </div>
  );
};
